<template>
  <WdsButtonGhost
    v-if="!showJobBudget"
    secondary
    type="button"
    data-test="add-budget"
    @click="handleClick(true)"
  >
    <WdsStack gap-sm align-center>
      <WdsIcon name="plus"></WdsIcon>
      <span>{{ $t('Add budget') }}</span>
    </WdsStack>
  </WdsButtonGhost>
  <template v-else>
    <WdsButtonGhost v-if="!jobBudgetRequired" secondary type="button" @click="handleClick(false)">
      <WdsStack gap-sm align-center>
        <WdsIcon name="subtract"></WdsIcon>
        <span>{{ $t('Remove budget') }}</span>
      </WdsStack>
    </WdsButtonGhost>
    <br />
    <WdsStack style="gap: 1.5rem">
      <div class="tw-w-1/2">
        <WdsText secondary class="tw-mb-2.5">
          <strong>{{ $t('Job budget') }}</strong>
          <span v-if="!jobBudgetRequired">{{ $t('Optional') }}</span>
        </WdsText>
        <WdsInputCurrency
          v-model="jobBudgetStr"
          :config="currencyConfig"
          :placeholder="$t('0.00')"
          :info="
            jobBudgetError === null
              ? $t(
                  'A job budget allows you to track internal spending and is not visible to the worker',
                )
              : null
          "
          :error="$t(jobBudgetError)"
          data-test="job-budget-input"
        />
      </div>
      <div v-if="projectsEnabled" class="tw-w-1/2">
        <WdsText secondary class="tw-mb-2.5">
          <strong>{{ $t('Project association') }}</strong>
          {{ $t('Optional') }}
        </WdsText>
        <WdsSelect
          v-model="projectId"
          can-deselect
          searchable
          :list="mapper"
          :placeholder="$t('Select a project')"
          :info="
            projectsError === null
              ? $t(
                  'Select a project, if you wish to associate this job to an existing project and its budget.',
                )
              : null
          "
          :load-more-distance="10"
          name="associated_project_id"
          :error="$t(projectsError)"
          @load-more="getMore"
          @search="search = $event"
        >
          <template v-if="loading" #prefix>
            <WdsSpinner indeterminate :size="16" />
          </template>
        </WdsSelect>
      </div>
    </WdsStack>
    <input
      v-if="jobBudgetPrimitive"
      type="hidden"
      name="internal_budget"
      :value="jobBudgetPrimitive"
      :required="jobBudgetRequired"
    />
  </template>
</template>

<script setup lang="ts">
import gql from 'graphql-tag'
import {
  WdsInputCurrency,
  WdsSelect,
  WdsIcon,
  WdsText,
  WdsStack,
  WdsButtonGhost,
  WdsSpinner,
} from '@worksome/wds-components'
import { computed, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { convertToPrimitive, flat } from '@/utils/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps({
  currency: {
    type: String,
    default: () => currentAccount.currency,
  },
  project: {
    type: Object,
    default: null,
  },
  jobBudget: {
    type: String,
    default: null,
  },
  jobBudgetRequired: {
    type: Boolean,
    default: false,
  },
  jobBudgetError: {
    type: String,
    default: null,
  },
  projectsEnabled: {
    type: Boolean,
    default: false,
  },
  projectsError: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:budget', 'update:project'])

const jobBudgetStr = ref(`${props.jobBudget}`)

const jobBudgetPrimitive = computed(() =>
  jobBudgetStr.value === null ? null : convertToPrimitive(jobBudgetStr.value, String as any),
)

watch(jobBudgetPrimitive, (budget) => emit('update:budget', budget))

const currencyConfig = ref({
  currencySymbol: props.currency,
  thousandsSeparator: ',',
  decimalSeparator: '.',
  showFractions: true,
})

const projectIn = props.project
  ? { value: btoa(`Project:${props.project.id}`), label: props.project.name }
  : null

const projectId = ref(projectIn ?? {})
// eslint-disable-next-line @typescript-eslint/no-shadow -- Baseline eslint ignore please fix if working on this
watch(projectId, (projectId) => emit('update:project', projectId))

const search = ref<string>('')

const displayJobClicked = ref(false)

const showJobBudget = computed(
  () =>
    props.jobBudgetRequired ||
    displayJobClicked.value ||
    (props.jobBudget !== null && !displayJobClicked.value),
)

const { result, loading, fetchMore, options } = useQuery(
  gql`
    query Projects($accounts: [ID!], $page: Int, $count: Int, $search: String) {
      projects(accounts: $accounts, first: $count, page: $page, search: $search) {
        data {
          id
          name
          description
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  {
    accounts: [currentAccount.globalId],
    count: 50,
    page: 1,
    search,
  },
  {
    clientId: 'publicApiCustomFields',
    debounce: 100,
    enabled: props.projectsEnabled,
  },
)

// The enabled option in useQuery does not seem to be reactive, so manually adjust if the query is enabled instead
watch(
  () => props.projectsEnabled,
  (enabled) => {
    // @ts-ignore
    options.enabled = enabled
  },
)

async function getMore() {
  if (!loading.value) {
    // This logic prevents accidentally loading in an old page of data resulting in duplicated data
    const { hasMorePages, currentPage } = flat(result.value ?? {}) ?? {}
    if (!hasMorePages) return

    fetchMore({
      variables: {
        page: currentPage + 1,
      },
    })
  }
}

let previousResult = {}
const mapper = computed(() => {
  const list: any[] = []

  if (projectIn) {
    list.push(projectIn)
  }

  const res = loading.value ? previousResult : result.value
  previousResult = res
  const { data: projects } = flat(res?.projects ?? {}) ?? {}

  if (projects) {
    return list.concat(
      projects
        .filter((project) => project.id !== projectIn?.value)
        .map((project) => ({ label: `${project.name}`, value: project.id })),
    )
  }
  return list
})

const handleClick = (newDisplayJobClickedValue) => {
  displayJobClicked.value = newDisplayJobClickedValue

  if (!displayJobClicked.value) {
    jobBudgetStr.value = props.jobBudget ?? null
  }
}
</script>

<style scoped lang="scss">
strong {
  @apply tw-text-gray-900;
  @apply tw-mr-2;
}
</style>
